import React, { useContext, useMemo, useState } from 'react';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import useMutation from '../../../../../../hooks/useMutation';
import { useToast } from '../../../../../../providers/ToastProvider';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import { User } from '../../../../../../types/user';
import SaveChangesButton from '../../../../../components/Button/templates/SaveChangesButton';
import { Surface } from '../../../../../components/Surface';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { CustomFieldEntityType } from '../../../../../../types/custom_field';
import CustomFieldInput from '../../../../Common/CustomFieldInput';
import { CompanyRoleAssignmentContext } from '../../../../../../context/CompanyRoleAssignmentContext';
import { CompanyRoleAssignmentInput } from '../../../../../../types/company_role_assignment/companyRoleAssignment.input';
import {
  CompanyRoleAssignmentMutations,
  UpdateCompanyRoleAssignmentResponse,
  UpdateCompanyRoleAssignmentVariables,
} from '../../../../../../graphql/companyRoleAssignment.graphql';
import { ToastType } from '../../../../../components/Toast';
import { toMap } from '../../../../../../util/map.util';
import { CompanyRoleAssignment } from '../../../../../../types/company_role_assignment';
import { UserContext } from '../../../../../../context/UserContext';
import { ContactContext } from '../../../../../../context/ContactContext';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';

interface UserUpdateCustomFIeldPaneProps {
  user: User;
}

export default function UserUpdateCustomFieldPane({ user }: UserUpdateCustomFIeldPaneProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { customFields } = useContext(CustomFieldContext);
  const { companyRoles, setCompanyRoles } = useContext(CompanyRoleAssignmentContext);
  const { companyUsers } = useContext(UserContext);
  const { contacts } = useContext(ContactContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const original = useMemo(() => {
    return companyRoles.get(user.id)?.find(item => item.companyId === currentCompany.id);
  }, [companyRoles]);

  const [input, setInput] = useState(
    new CompanyRoleAssignmentInput(original || { companyId: currentCompany.id, userId: user.id }),
  );

  const [update, { loading }] = useMutation<UpdateCompanyRoleAssignmentResponse, UpdateCompanyRoleAssignmentVariables>(
    CompanyRoleAssignmentMutations.update,
    {
      variables: { companyRoleAssignment: input.forUpdate() },
      onCompleted: response => {
        const userId = response.updateCompanyRoleAssignment.userId;
        const forUser = toMap(companyRoles.get(userId) || [], 'id').set(
          response.updateCompanyRoleAssignment.id,
          new CompanyRoleAssignment(response.updateCompanyRoleAssignment),
        );
        setInput(new CompanyRoleAssignmentInput(response.updateCompanyRoleAssignment));
        setCompanyRoles(new Map(companyRoles).set(userId, [...forUser.values()]));
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.userUpdatedSuccessfullyToastTitle).singular.upper,
        });
      },
    },
  );

  const relevantCustomFields = useMemo(() => {
    return [...customFields.values()]
      .filter(cf => cf.entityType === CustomFieldEntityType.companyRole && !cf.hidden)
      .sort((a, b) => (a.mandatory ? -1 : a.index - b.index));
  }, [customFields]);

  return (
    <div>
      <Surface>
        <div className='grid gap-4'>
          <div className='grid gap-2'>
            <p className='section-title'>{t(TranslationKey.customFields).singular.upper}</p>
          </div>
          <div className='grid grid-cols-4 gap-2'>
            {relevantCustomFields.map(cf => (
              <div className='w-full'>
                <CustomFieldInput
                  item={cf}
                  value={input.customFields.get(cf.id)?.value}
                  entity={input}
                  onChange={setInput}
                  users={companyUsers}
                  contacts={contacts}
                  productMasterData={productMasterData}
                />
              </div>
            ))}
          </div>
          <SaveChangesButton
            disabled={!input.validate(customFields) || !input.hasChanges(original)}
            loading={loading}
            className='w-fit'
            onClick={() => update()}
          />
        </div>
      </Surface>
    </div>
  );
}
